import type {
  postDealerPortalV1DealerIdBorrowerBorIdEmploymentResponse,
  putDealerPortalV1DealerIdBorrowerBorIdEmploymentEmpIdUpdateEmploymentResponse,
} from "@porchpass/api-client";
import { ApiError, DomainHousingType } from "@porchpass/api-client";
import type { Address, BorrowerData } from "../types/applicationTypes";

/**
 * Pure, dependency-injected helpers shared by the auto-save handlers. They take
 * the store/api surfaces they need as plain arguments so they can be unit-tested
 * with fakes — they never touch React or component scope.
 */

/**
 * Check if an entity has at least one user-entered field worth saving.
 * Used to avoid POSTing completely empty entities.
 */
export const hasFilledFields = (
  entity: object,
  skipKeys = ["id", "serverId"],
): boolean =>
  Object.entries(entity).some(
    ([k, v]) =>
      !skipKeys.includes(k) && v !== undefined && v !== null && v !== "",
  );

/**
 * Process pending deletes individually. Each delete gets its own try/catch so
 * one failure doesn't block others. 404s are treated as success (entity already
 * deleted server-side).
 */
export const processPendingDeletes = async (
  store: {
    pendingEntityDeletes: Record<string, string[]>;
    clearPendingEntityDeletes: (key: string) => void;
    queueEntityDelete: (
      borrowerType: "primary" | "co",
      entityType: string,
      serverId: string,
    ) => void;
  },
  deleteKey: string,
  deleteFn: (id: string) => Promise<unknown>,
) => {
  const pendingDeletes = store.pendingEntityDeletes[deleteKey] || [];
  if (pendingDeletes.length === 0) return;
  const succeeded: string[] = [];
  for (const serverId of pendingDeletes) {
    try {
      await deleteFn(serverId);
      succeeded.push(serverId);
    } catch (e) {
      const status =
        e && typeof e === "object" && "status" in e
          ? (e as { status: number }).status
          : undefined;
      if (status === 404) {
        // Entity already deleted server-side — treat as success
        succeeded.push(serverId);
      } else {
        console.error(
          `[autosave] delete failed for ${deleteKey}/${serverId}:`,
          e,
        );
      }
    }
  }
  if (succeeded.length === pendingDeletes.length) {
    store.clearPendingEntityDeletes(deleteKey);
  } else if (succeeded.length > 0) {
    const failed = pendingDeletes.filter((id) => !succeeded.includes(id));
    store.clearPendingEntityDeletes(deleteKey);
    for (const id of failed) {
      store.queueEntityDelete(
        deleteKey.startsWith("co.") ? "co" : "primary",
        deleteKey.split(".").slice(1).join("."),
        id,
      );
    }
  }
};

export const saveDirtyAddresses = async (opts: {
  borrowerId: string;
  addresses: Address[];
  indicesToSave: number[];
  fieldPrefix: "primaryBorrower" | "coBorrower";
  borrowerType: "primary" | "co";
  api: {
    updateAddress: (
      borrowerId: string,
      addressServerId: string,
      address: Address,
    ) => Promise<{ data: unknown }>;
    createAddress: (
      borrowerId: string,
      address: Address,
    ) => Promise<{ data: unknown }>;
  };
  store: {
    setEntityServerId: (
      borrowerType: "primary" | "co",
      entityType: "addresses",
      clientId: string,
      serverId: string,
    ) => void;
    setBorrowerFieldWithoutDirty: (
      borrowerType: "primary" | "co",
      updates: Partial<BorrowerData>,
    ) => void;
    setFieldError: (field: string, message: string) => void;
  };
}) => {
  const {
    borrowerId,
    addresses,
    indicesToSave,
    fieldPrefix,
    borrowerType,
    api,
    store,
  } = opts;

  for (const i of indicesToSave) {
    const addr = addresses[i];
    if (!addr) continue;
    if (
      (addr.housingType === DomainHousingType.HousingOther ||
        addr.housingType === DomainHousingType.HousingOwn) &&
      !addr.housingTypeOther
    )
      continue;

    try {
      if (addr.serverId) {
        const resp = await api.updateAddress(borrowerId, addr.serverId, addr);
        const addrData = resp.data as {
          has_sufficient_address_history?: boolean;
        };
        if (addrData.has_sufficient_address_history !== undefined) {
          store.setBorrowerFieldWithoutDirty(borrowerType, {
            hasSufficientAddressHistory:
              addrData.has_sufficient_address_history,
          });
        }
      } else {
        const resp = await api.createAddress(borrowerId, addr);
        const addrData = resp.data as {
          address_id?: string;
          has_sufficient_address_history?: boolean;
        };
        if (addrData.address_id && addr.id) {
          store.setEntityServerId(
            borrowerType,
            "addresses",
            addr.id,
            addrData.address_id,
          );
        }
        if (addrData.has_sufficient_address_history !== undefined) {
          store.setBorrowerFieldWithoutDirty(borrowerType, {
            hasSufficientAddressHistory:
              addrData.has_sufficient_address_history,
          });
        }
      }
    } catch (e) {
      console.error(`[autosave] address save failed (index ${i}):`, e);
      const message =
        e instanceof ApiError
          ? (e.message ?? "Failed to save address")
          : "Failed to save address";
      store.setFieldError(`${fieldPrefix}.addresses.${i}`, message);
    }
  }
};

export const updateSufficientEmploymentHistory = (
  resp:
    | putDealerPortalV1DealerIdBorrowerBorIdEmploymentEmpIdUpdateEmploymentResponse
    | postDealerPortalV1DealerIdBorrowerBorIdEmploymentResponse,
  updateFn: (updates: Partial<BorrowerData>) => void,
) => {
  if (resp.status !== 200) return;

  const empData = resp.data as {
    employment_id?: string;
    has_sufficient_employment_history?: boolean | null;
  };

  if (
    empData.has_sufficient_employment_history !== undefined &&
    empData.has_sufficient_employment_history !== null
  ) {
    updateFn({
      hasSufficientEmploymentHistory: empData.has_sufficient_employment_history,
    });
  }
};
